/**
 * File manager a cartelle del modulo documentale.
 *
 * Layout:
 * - Sidebar sinistra: albero cartelle navigabile per unità organizzativa
 * - Area principale: contenuto della cartella selezionata
 * - Toolbar: upload, nuova cartella, elimina
 * - Breadcrumb del percorso corrente
 * - Context menu su cartelle e file: rinomina, taglia, copia, incolla, elimina
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormatters } from '@lib/formatters';

interface Cartella {
  id: string;
  parent_id: string | null;
  unita_organizzativa_id: string | null;
  nome: string;
  percorso_completo: string;
  tipo: 'sistema' | 'utente';
  num_documenti: number;
  num_sotto_cartelle: number;
  created_at: string;
}

interface DocumentoArchivio {
  id: string;
  year: number;
  tipo: string;
  sottotipo: string | null;
  stato: string;
  file_path: string | null;
  file_hash: string | null;
  mime_type: string | null;
  dimensione: number | null;
  livello_sensibilita: string;
  cartella_id: string | null;
  created_at: string;
}

interface CestinoItem {
  id: string;
  year: number;
  sottotipo: string | null;
  file_path: string | null;
  mime_type: string | null;
  dimensione: number | null;
  deleted_at: string;
  created_at: string;
}

interface UnitaOrganizzativa {
  id: string;
  nome: string;
}

type ClipboardMode = 'taglia' | 'copia';

interface ClipboardItem {
  doc: DocumentoArchivio;
  mode: ClipboardMode;
}

const headers = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/** Formatta byte in unità leggibile. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Icona per tipo MIME. */
function mimeIcon(mime: string | null): string {
  if (!mime) return '📄';
  if (mime.startsWith('image/')) return '🖼️';
  if (mime === 'application/pdf') return '📕';
  if (mime.includes('spreadsheet') || mime.includes('excel')) return '📊';
  if (mime.includes('word') || mime.includes('document')) return '📝';
  if (mime.includes('zip') || mime.includes('compressed')) return '📦';
  if (mime.includes('xml')) return '📋';
  return '📄';
}

/** Estrai nome file dal path rimuovendo il prefisso UUID. */
function nomeFileDaPath(path: string | null): string {
  if (!path) return '—';
  const nome = path.split('/').pop() ?? path;
  if (nome.includes('_')) {
    const parti = nome.split('_');
    if (parti[0].length === 36) return parti.slice(1).join('_');
  }
  return nome;
}

/**
 * File manager a cartelle — pagina principale archivio documentale.
 */
const ArchivioExplorer: React.FC = () => {
  const fmt = useFormatters();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Stato
  const [unita, setUnita] = useState<UnitaOrganizzativa[]>([]);
  const [unitaSelezionata, setUnitaSelezionata] = useState<string>('__all__');
  const [cartelle, setCartelle] = useState<Cartella[]>([]);
  const [cartellaCorrente, setCartellaCorrente] = useState<Cartella | null>(null);
  const [documenti, setDocumenti] = useState<DocumentoArchivio[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vistaCestino, setVistaCestino] = useState(false);
  const [cestino, setCestino] = useState<CestinoItem[]>([]);
  const [uploading, setUploading] = useState(false);

  // Anteprima file
  const [preview, setPreview] = useState<{
    doc: DocumentoArchivio;
    blobUrl: string | null;
    textContent: string | null;
    loading: boolean;
  } | null>(null);

  // Clipboard (taglia/copia)
  const [clipboard, setClipboard] = useState<ClipboardItem | null>(null);

  // Context menu posizione
  const [ctxMenu, setCtxMenu] = useState<{
    x: number;
    y: number;
    tipo: 'cartella' | 'file';
    cartella?: Cartella;
    doc?: DocumentoArchivio;
  } | null>(null);

  // Chiudi context menu su click fuori + ESC chiude anteprima
  useEffect(() => {
    const clickHandler = () => setCtxMenu(null);
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && preview) {
        if (preview.blobUrl) URL.revokeObjectURL(preview.blobUrl);
        setPreview(null);
      }
    };
    document.addEventListener('click', clickHandler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('click', clickHandler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [preview]);

  // Carica unità organizzative
  useEffect(() => {
    const caricaUnita = async () => {
      try {
        const res = await fetch('/api/v1/unita', { headers: headers() });
        if (res.ok) {
          const data: UnitaOrganizzativa[] = await res.json();
          setUnita(data);
          if (data.length > 0) {
            setUnitaSelezionata(data[0].id);
          }
        }
      } catch {
        // Silenzioso — le unità non sono critiche
      }
    };
    void caricaUnita();
  }, []);

  // Carica cartelle quando cambia unità
  const caricaCartelle = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (unitaSelezionata !== '__all__') {
        params.set('unita_organizzativa_id', unitaSelezionata);
      }

      const res = await fetch(`/api/v1/archivio/cartelle?${params}`, {
        headers: headers(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: Cartella[] = await res.json();
      setCartelle(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento cartelle');
    }
  }, [unitaSelezionata]);

  useEffect(() => {
    void caricaCartelle();
  }, [caricaCartelle]);

  // Carica documenti della cartella selezionata
  const caricaDocumenti = useCallback(async () => {
    if (!cartellaCorrente) {
      setDocumenti([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({
        cartella_id: cartellaCorrente.id,
      });
      const res = await fetch(`/api/v1/archivio?${params}`, {
        headers: headers(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: DocumentoArchivio[] = await res.json();
      setDocumenti(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento documenti');
    } finally {
      setLoading(false);
    }
  }, [cartellaCorrente]);

  useEffect(() => {
    void caricaDocumenti();
  }, [caricaDocumenti]);

  // Albero cartelle: root = parent_id null
  const cartelleRoot = cartelle.filter((c) => c.parent_id === null);
  const sottocartelle = (parentId: string) =>
    cartelle.filter((c) => c.parent_id === parentId);

  // Breadcrumb
  const buildBreadcrumb = (): Cartella[] => {
    if (!cartellaCorrente) return [];
    const trail: Cartella[] = [];
    let current: Cartella | undefined = cartellaCorrente;
    while (current) {
      trail.unshift(current);
      current = cartelle.find((c) => c.id === current!.parent_id);
    }
    return trail;
  };

  // ====================================================================
  // AZIONI CARTELLE
  // ====================================================================

  const creaNuovaCartella = async () => {
    const dove = cartellaCorrente
      ? `in "${cartellaCorrente.percorso_completo}"`
      : 'nella radice';
    const nome = prompt(`Nuova cartella ${dove}.\nNome:`);
    if (!nome || !nome.trim()) return;
    try {
      const body: Record<string, unknown> = {
        nome: nome.trim(),
        parent_id: cartellaCorrente?.id ?? null,
      };
      if (unitaSelezionata !== '__all__') {
        body.unita_organizzativa_id = unitaSelezionata;
      } else if (cartellaCorrente?.unita_organizzativa_id) {
        body.unita_organizzativa_id = cartellaCorrente.unita_organizzativa_id;
      } else if (unita.length === 1) {
        body.unita_organizzativa_id = unita[0].id;
      }
      const res = await fetch('/api/v1/archivio/cartelle', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      await caricaCartelle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore creazione cartella');
    }
  };

  const eliminaCartella = async (cartellaId: string) => {
    if (!confirm('Eliminare questa cartella?')) return;
    try {
      const res = await fetch(`/api/v1/archivio/cartelle/${cartellaId}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      if (cartellaCorrente?.id === cartellaId) {
        const padre = cartelle.find((c) => c.id === cartellaCorrente?.parent_id);
        setCartellaCorrente(padre ?? null);
      }
      await caricaCartelle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore eliminazione');
    }
  };

  // ====================================================================
  // AZIONI FILE
  // ====================================================================

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !cartellaCorrente) return;

    setUploading(true);
    setError(null);

    try {
      for (const file of Array.from(files) as File[]) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('cartella_id', cartellaCorrente.id);

        const res = await fetch('/api/v1/archivio/upload', {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token') ?? ''}` },
          body: formData,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Errore upload ${file.name}`);
        }
      }
      await caricaDocumenti();
      await caricaCartelle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const eliminaDocumento = async (docId: string, year: number) => {
    if (!confirm('Eliminare questo documento?')) return;
    try {
      const res = await fetch(`/api/v1/archivio/${docId}?year=${year}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      await caricaDocumenti();
      await caricaCartelle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore eliminazione');
    }
  };

  const downloadFile = async (docId: string, year: number) => {
    try {
      const res = await fetch(`/api/v1/archivio/download/${docId}?year=${year}`, {
        headers: headers(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const blob = await res.blob();
      const disposition = res.headers.get('content-disposition');
      const nomeFile =
        disposition?.split('filename=')[1]?.replace(/"/g, '') ?? 'file';
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = nomeFile;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore download');
    }
  };

  // ====================================================================
  // CLIPBOARD — Taglia / Copia / Incolla
  // ====================================================================

  // Anteprima file
  const apriAnteprima = async (doc: DocumentoArchivio) => {
    if (!doc.file_path) return;
    setPreview({ doc, blobUrl: null, textContent: null, loading: true });

    try {
      const res = await fetch(`/api/v1/archivio/download/${doc.id}?year=${doc.year}`, {
        headers: headers(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);

      const mime = doc.mime_type ?? '';
      const isText = mime.startsWith('text/') || mime.includes('xml') || mime.includes('csv') || mime.includes('json');
      const isPreviewable = mime.startsWith('image/') || mime === 'application/pdf' || isText;

      if (!isPreviewable) {
        setPreview({ doc, blobUrl: null, textContent: null, loading: false });
        return;
      }

      if (isText) {
        const text = await res.text();
        setPreview({ doc, blobUrl: null, textContent: text, loading: false });
      } else {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        setPreview({ doc, blobUrl: url, textContent: null, loading: false });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore anteprima');
      setPreview(null);
    }
  };

  const chiudiAnteprima = () => {
    if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  };

  const tagliFile = (doc: DocumentoArchivio) => {
    setClipboard({ doc, mode: 'taglia' });
  };

  const copiaFile = (doc: DocumentoArchivio) => {
    setClipboard({ doc, mode: 'copia' });
  };

  const incolla = async () => {
    if (!clipboard || !cartellaCorrente) return;
    try {
      if (clipboard.mode === 'taglia') {
        // Sposta: PATCH /archivio/{id}
        const res = await fetch(
          `/api/v1/archivio/${clipboard.doc.id}?year=${clipboard.doc.year}`,
          {
            method: 'PATCH',
            headers: { ...headers(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartella_id: cartellaCorrente.id }),
          },
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Errore ${res.status}`);
        }
      } else {
        // Copia: POST /archivio/{id}/copia
        const res = await fetch(
          `/api/v1/archivio/${clipboard.doc.id}/copia?year=${clipboard.doc.year}`,
          {
            method: 'POST',
            headers: { ...headers(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartella_id: cartellaCorrente.id }),
          },
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `Errore ${res.status}`);
        }
      }
      setClipboard(null);
      await caricaDocumenti();
      await caricaCartelle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore incolla');
    }
  };

  // ====================================================================
  // CESTINO
  // ====================================================================

  const caricaCestino = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/archivio/cestino', { headers: headers() });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      const data: CestinoItem[] = await res.json();
      setCestino(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore caricamento cestino');
    }
  }, []);

  useEffect(() => {
    if (vistaCestino) void caricaCestino();
  }, [vistaCestino, caricaCestino]);

  const ripristinaDocumento = async (docId: string, year: number) => {
    try {
      const res = await fetch(`/api/v1/archivio/cestino/${docId}/ripristina?year=${year}`, {
        method: 'POST',
        headers: headers(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      await caricaCestino();
      await caricaCartelle();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore ripristino');
    }
  };

  const eliminaDefinitivo = async (docId: string, year: number) => {
    if (!confirm('Eliminare definitivamente? Il file verrà rimosso dal disco.')) return;
    try {
      const res = await fetch(`/api/v1/archivio/cestino/${docId}?year=${year}`, {
        method: 'DELETE',
        headers: headers(),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      await caricaCestino();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore eliminazione');
    }
  };

  const svuotaCestino = async () => {
    if (!confirm(`Svuotare il cestino? ${cestino.length} file verranno eliminati definitivamente dal disco.`)) return;
    try {
      const res = await fetch('/api/v1/archivio/cestino/svuota', {
        method: 'POST',
        headers: headers(),
      });
      if (!res.ok) throw new Error(`Errore ${res.status}`);
      await caricaCestino();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore svuotamento cestino');
    }
  };

  // ====================================================================
  // CONTEXT MENU (tasto destro)
  // ====================================================================

  const handleContextMenuCartella = (e: React.MouseEvent, cartella: Cartella) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ x: e.clientX, y: e.clientY, tipo: 'cartella', cartella });
  };

  const handleContextMenuFile = (e: React.MouseEvent, doc: DocumentoArchivio) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ x: e.clientX, y: e.clientY, tipo: 'file', doc });
  };

  const apriRinominaCartella = async (c: Cartella) => {
    setCtxMenu(null);
    const nuovoNome = prompt('Rinomina cartella:', c.nome);
    if (!nuovoNome || nuovoNome.trim() === '' || nuovoNome.trim() === c.nome) return;
    try {
      const res = await fetch(`/api/v1/archivio/cartelle/${c.id}`, {
        method: 'PATCH',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ nome: nuovoNome.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      await caricaCartelle();
      if (cartellaCorrente?.id === c.id) {
        const updated: Cartella = await res.json().catch(() => null);
        if (updated) setCartellaCorrente(updated);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore rinomina cartella');
    }
  };

  const apriRinominaFile = async (doc: DocumentoArchivio) => {
    setCtxMenu(null);
    const nomeAttuale = nomeFileDaPath(doc.file_path);
    const nuovoNome = prompt('Rinomina file:', nomeAttuale);
    if (!nuovoNome || nuovoNome.trim() === '' || nuovoNome.trim() === nomeAttuale) return;
    try {
      const res = await fetch(
        `/api/v1/archivio/${doc.id}/rinomina?year=${doc.year}`,
        {
          method: 'POST',
          headers: { ...headers(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ nome: nuovoNome.trim() }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Errore ${res.status}`);
      }
      await caricaDocumenti();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore rinomina file');
    }
  };

  // ====================================================================
  // COMPONENTI INTERNI
  // ====================================================================

  // Componente albero ricorsivo
  const TreeNode: React.FC<{ cartella: Cartella; depth: number }> = ({
    cartella,
    depth,
  }) => {
    const isSelected = cartellaCorrente?.id === cartella.id;
    const figli = sottocartelle(cartella.id);
    const [expanded, setExpanded] = useState(depth < 1);

    return (
      <div>
        <button
          className={`flex w-full items-center gap-1 rounded px-2 py-1 text-left text-sm hover:bg-muted/50 ${
            isSelected ? 'bg-primary/10 font-semibold text-primary' : ''
          }`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => {
            setCartellaCorrente(cartella);
            setVistaCestino(false);
            if (figli.length > 0) setExpanded(!expanded);
          }}
          onContextMenu={(e) => handleContextMenuCartella(e, cartella)}
        >
          <span className="shrink-0">
            {figli.length > 0 ? (expanded ? '▾' : '▸') : '  '}
          </span>
          <span className="shrink-0">
            {cartella.tipo === 'sistema' ? '📁' : '📂'}
          </span>
          <span className="truncate">{cartella.nome}</span>
          {cartella.num_documenti > 0 && (
            <Badge variant="secondary" className="ml-auto text-xs">
              {cartella.num_documenti}
            </Badge>
          )}
        </button>
        {expanded &&
          figli.map((figlio) => (
            <TreeNode key={figlio.id} cartella={figlio} depth={depth + 1} />
          ))}
      </div>
    );
  };

  const breadcrumb = buildBreadcrumb();

  // ====================================================================
  // RENDER
  // ====================================================================

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-4 p-6">
      {/* Sidebar — albero cartelle */}
      <div className="flex w-64 shrink-0 flex-col rounded-lg border bg-card">
        {/* Switcher unità */}
        <div className="border-b p-3">
          <Select value={unitaSelezionata} onValueChange={setUnitaSelezionata}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Unità organizzativa" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Tutte le unità</SelectItem>
              {unita.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Albero */}
        <div className="flex-1 overflow-y-auto p-2">
          {cartelleRoot.map((c) => (
            <TreeNode key={c.id} cartella={c} depth={0} />
          ))}
          {cartelleRoot.length === 0 && (
            <p className="p-4 text-center text-xs text-muted-foreground">
              Nessuna cartella
            </p>
          )}
        </div>

        {/* Bottone Cestino in fondo alla sidebar */}
        <div className="border-t p-2">
          <button
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-muted/50 ${
              vistaCestino ? 'bg-primary/10 font-semibold text-primary' : ''
            }`}
            onClick={() => {
              setVistaCestino(!vistaCestino);
              setCartellaCorrente(null);
            }}
          >
            🗑️ Cestino
            {cestino.length > 0 && (
              <Badge variant="secondary" className="ml-auto text-xs">
                {cestino.length}
              </Badge>
            )}
          </button>
        </div>
      </div>

      {/* Area principale */}
      <div className="flex flex-1 flex-col">
        {/* Toolbar + breadcrumb */}
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <button
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setCartellaCorrente(null)}
            >
              Archivio
            </button>
            {breadcrumb.map((c) => (
              <React.Fragment key={c.id}>
                <span className="text-muted-foreground">/</span>
                <button
                  className={`hover:text-foreground ${
                    c.id === cartellaCorrente?.id
                      ? 'font-semibold text-foreground'
                      : 'text-muted-foreground'
                  }`}
                  onClick={() => setCartellaCorrente(c)}
                >
                  {c.nome}
                </button>
              </React.Fragment>
            ))}
          </div>

          <div className="flex gap-2">
            {/* Indicatore clipboard */}
            {clipboard && (
              <div className="flex items-center gap-1 rounded border border-dashed border-primary/40 bg-primary/5 px-2 py-1 text-xs text-primary">
                {clipboard.mode === 'taglia' ? '✂️' : '📋'}{' '}
                {nomeFileDaPath(clipboard.doc.file_path)}
                <button
                  className="ml-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setClipboard(null)}
                >
                  ✕
                </button>
              </div>
            )}

            {/* Bottone Incolla */}
            {clipboard && cartellaCorrente && (
              <Button variant="outline" size="sm" onClick={incolla}>
                📋 Incolla
              </Button>
            )}

            {/* Nuova cartella */}
            <Button variant="outline" size="sm" onClick={creaNuovaCartella}>
              + Cartella
            </Button>

            {cartellaCorrente?.tipo === 'utente' && (
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:bg-destructive/10"
                onClick={() => eliminaCartella(cartellaCorrente.id)}
              >
                Elimina cartella
              </Button>
            )}

            <Button
              size="sm"
              disabled={!cartellaCorrente || uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Upload…' : '↑ Upload'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              onChange={handleUpload}
            />
          </div>
        </div>

        {/* Errore */}
        {error && (
          <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
            <button
              className="ml-2 underline"
              onClick={() => setError(null)}
            >
              chiudi
            </button>
          </div>
        )}

        {/* Contenuto */}
        <Card className="flex-1 overflow-hidden">
          <CardContent className="h-full overflow-y-auto p-0">
            {vistaCestino ? (
              /* --- Vista cestino --- */
              <div>
                <div className="flex items-center justify-between border-b p-4">
                  <p className="text-sm font-semibold">
                    🗑️ Cestino ({cestino.length} elementi)
                  </p>
                  {cestino.length > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={svuotaCestino}
                    >
                      Svuota cestino
                    </Button>
                  )}
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Nome file</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Dimensione</TableHead>
                      <TableHead>Eliminato il</TableHead>
                      <TableHead className="w-32"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cestino.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-sm text-muted-foreground"
                        >
                          Il cestino è vuoto
                        </TableCell>
                      </TableRow>
                    )}
                    {cestino.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>{mimeIcon(doc.mime_type)}</TableCell>
                        <TableCell className="max-w-xs truncate text-sm">
                          {nomeFileDaPath(doc.file_path)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {doc.sottotipo ?? '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {doc.dimensione != null ? formatBytes(doc.dimensione) : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {fmt.date(doc.deleted_at)}
                        </TableCell>
                        <TableCell className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => ripristinaDocumento(doc.id, doc.year)}
                          >
                            Ripristina
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => eliminaDefinitivo(doc.id, doc.year)}
                          >
                            ✕
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : !cartellaCorrente ? (
              <div className="flex h-full items-center justify-center text-muted-foreground">
                <p>Seleziona una cartella dalla sidebar</p>
              </div>
            ) : loading ? (
              <p className="p-6 text-center text-sm text-muted-foreground">
                Caricamento…
              </p>
            ) : (
              <>
                {/* Sotto-cartelle */}
                {sottocartelle(cartellaCorrente.id).length > 0 && (
                  <div className="border-b p-4">
                    <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                      Cartelle
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {sottocartelle(cartellaCorrente.id).map((sub) => (
                        <button
                          key={sub.id}
                          className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm hover:bg-muted/50"
                          onClick={() => setCartellaCorrente(sub)}
                          onContextMenu={(e) => handleContextMenuCartella(e, sub)}
                        >
                          📁 {sub.nome}
                          {sub.num_documenti > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {sub.num_documenti}
                            </Badge>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Documenti */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Nome file</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Dimensione</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead className="w-28"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documenti.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="text-center text-sm text-muted-foreground"
                        >
                          Cartella vuota
                        </TableCell>
                      </TableRow>
                    )}
                    {documenti.map((doc) => (
                      <TableRow
                        key={doc.id}
                        className={
                          clipboard?.doc.id === doc.id && clipboard.mode === 'taglia'
                            ? 'opacity-40'
                            : ''
                        }
                        onContextMenu={(e) => handleContextMenuFile(e, doc)}
                      >
                        <TableCell>{mimeIcon(doc.mime_type)}</TableCell>
                        <TableCell className="max-w-xs truncate text-sm font-medium">
                          {doc.file_path ? (
                            <button
                              className="text-left text-primary hover:underline"
                              onClick={() => apriAnteprima(doc)}
                            >
                              {nomeFileDaPath(doc.file_path)}
                            </button>
                          ) : (
                            nomeFileDaPath(doc.file_path)
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {doc.sottotipo ?? doc.mime_type?.split('/').pop() ?? '—'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {doc.dimensione != null
                            ? formatBytes(doc.dimensione)
                            : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {fmt.date(doc.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {/* Azioni rapide */}
                            {doc.file_path && (
                              <Button
                                variant="ghost"
                                size="sm"
                                title="Scarica"
                                onClick={() => downloadFile(doc.id, doc.year)}
                              >
                                ↓
                              </Button>
                            )}
                            {/* Menu azioni file */}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  ⋯
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => apriRinominaFile(doc)}>
                                  ✏️ Rinomina
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => tagliFile(doc)}>
                                  ✂️ Taglia
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => copiaFile(doc)}>
                                  📋 Copia
                                </DropdownMenuItem>
                                {doc.file_path && (
                                  <DropdownMenuItem onClick={() => downloadFile(doc.id, doc.year)}>
                                    ↓ Scarica
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => eliminaDocumento(doc.id, doc.year)}
                                >
                                  🗑️ Elimina
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ================================================================
          CONTEXT MENU (tasto destro) — posizionato assoluto
          ================================================================ */}
      {ctxMenu && (
        <div
          className="fixed z-50 min-w-48 rounded-md border bg-popover p-1 shadow-md"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {ctxMenu.tipo === 'cartella' && ctxMenu.cartella && (
            <>
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                onClick={() => {
                  setCartellaCorrente(ctxMenu.cartella!);
                  setVistaCestino(false);
                  setCtxMenu(null);
                }}
              >
                📂 Apri
              </button>
              {ctxMenu.cartella.tipo === 'utente' && (
                <>
                  <button
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                    onClick={() => apriRinominaCartella(ctxMenu.cartella!)}
                  >
                    ✏️ Rinomina
                  </button>
                  <div className="my-1 h-px bg-border" />
                  <button
                    className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10"
                    onClick={() => {
                      eliminaCartella(ctxMenu.cartella!.id);
                      setCtxMenu(null);
                    }}
                  >
                    🗑️ Elimina
                  </button>
                </>
              )}
            </>
          )}
          {ctxMenu.tipo === 'file' && ctxMenu.doc && (
            <>
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                onClick={() => apriRinominaFile(ctxMenu.doc!)}
              >
                ✏️ Rinomina
              </button>
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                onClick={() => {
                  tagliFile(ctxMenu.doc!);
                  setCtxMenu(null);
                }}
              >
                ✂️ Taglia
              </button>
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                onClick={() => {
                  copiaFile(ctxMenu.doc!);
                  setCtxMenu(null);
                }}
              >
                📋 Copia
              </button>
              {ctxMenu.doc.file_path && (
                <button
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                  onClick={() => {
                    downloadFile(ctxMenu.doc!.id, ctxMenu.doc!.year);
                    setCtxMenu(null);
                  }}
                >
                  ↓ Scarica
                </button>
              )}
              <div className="my-1 h-px bg-border" />
              <button
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-destructive/10"
                onClick={() => {
                  eliminaDocumento(ctxMenu.doc!.id, ctxMenu.doc!.year);
                  setCtxMenu(null);
                }}
              >
                🗑️ Elimina
              </button>
            </>
          )}
        </div>
      )}

      {/* ================================================================
          PANNELLO ANTEPRIMA FILE
          ================================================================ */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={chiudiAnteprima}
        >
          <div
            className="relative flex h-[90vh] w-[90vw] max-w-5xl flex-col rounded-lg border bg-card shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header anteprima */}
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <span>{mimeIcon(preview.doc.mime_type)}</span>
                <span className="truncate">{nomeFileDaPath(preview.doc.file_path)}</span>
                {preview.doc.dimensione != null && (
                  <span className="text-muted-foreground">
                    ({formatBytes(preview.doc.dimensione)})
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadFile(preview.doc.id, preview.doc.year)}
                >
                  ↓ Scarica
                </Button>
                <Button variant="ghost" size="sm" onClick={chiudiAnteprima}>
                  ✕
                </Button>
              </div>
            </div>

            {/* Contenuto anteprima */}
            <div className="flex flex-1 items-center justify-center overflow-auto p-4">
              {preview.loading ? (
                <p className="text-muted-foreground">Caricamento anteprima…</p>
              ) : preview.textContent !== null ? (
                <pre className="h-full w-full overflow-auto whitespace-pre-wrap rounded bg-muted p-4 font-mono text-sm">
                  {preview.textContent}
                </pre>
              ) : preview.blobUrl && preview.doc.mime_type?.startsWith('image/') ? (
                <img
                  src={preview.blobUrl}
                  alt={nomeFileDaPath(preview.doc.file_path)}
                  className="max-h-full max-w-full object-contain"
                />
              ) : preview.blobUrl && preview.doc.mime_type === 'application/pdf' ? (
                <iframe
                  src={preview.blobUrl}
                  className="h-full w-full rounded border-0"
                  title={nomeFileDaPath(preview.doc.file_path)}
                />
              ) : (
                <div className="text-center text-muted-foreground">
                  <p className="mb-2 text-lg">{mimeIcon(preview.doc.mime_type)}</p>
                  <p className="mb-4">Anteprima non disponibile per questo tipo di file</p>
                  <Button
                    variant="outline"
                    onClick={() => downloadFile(preview.doc.id, preview.doc.year)}
                  >
                    ↓ Scarica il file
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArchivioExplorer;
